import { useState } from 'react'
import { toast } from 'react-toastify'

import {
  useGetExportStatsQuery,
  useGetProcessDataExportMutation,
} from '../../app/apiSliceProcessApi'
import { Button, Tabs } from '../../common/designs'
import { downloadBlobAsFile } from '../../common/utils/dataIntegrationUtils'
import ArisDataIngestionForm from '../ArisIntegrationAdminPanelPage/ArisDataIngestionForm'
import { ExportStatistics, StatusMessage } from './DataIntegrationComponents'
import { StatusMessageType } from './types'

interface Props {
  processId: string
}

const navTabs = [
  { label: 'CSV Export', value: 'csv-export' },
  { label: 'ARIS Data Ingestion API', value: 'aris-ingestion-api' },
]

const DataIntegrationForm = ({ processId }: Props) => {
  const [activeTabValue, setActiveTabValue] = useState<string>('csv-export')

  const {
    currentData: exportStats,
    isLoading,
    isFetching,
    error,
  } = useGetExportStatsQuery({ processId }, { skip: !processId })

  const [getProcessDataExport, { isLoading: isExporting }] = useGetProcessDataExportMutation()

  const handleExportClick = async (): Promise<void> => {
    if (!processId) return

    try {
      const response = await getProcessDataExport({ processId }).unwrap()

      if (response) {
        const filename = `Process_${processId}_export.csv`
        downloadBlobAsFile(response, filename)
        toast.success('Export completed successfully')
      } else {
        toast.error('No data received from export API')
      }
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Export failed. Please try again.')
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <Tabs
        size='s'
        options={navTabs}
        value={activeTabValue}
        onChange={(val: string) => setActiveTabValue(val)}
      />

      {activeTabValue === 'csv-export' && (
        <div className='flex flex-col gap-4'>
          {/* Loading State */}
          {(isLoading || isFetching) && (
            <StatusMessage
              type={StatusMessageType.LOADING}
              message='Loading export statistics...'
            />
          )}

          {/* Error State */}
          {error && (
            <StatusMessage
              type={StatusMessageType.ERROR}
              message='Failed to load export statistics. Please try again by refreshing the page.'
            />
          )}

          {/* Export Statistics */}
          {exportStats && <ExportStatistics stats={exportStats} />}

          {/* Export Button */}
          <Button
            onClick={handleExportClick}
            disabled={
              Boolean(error) ||
              !processId ||
              isLoading ||
              isFetching ||
              isExporting ||
              exportStats?.total_events === 0
            }
            loading={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export Process Data'}
          </Button>
        </div>
      )}

      {/* Ingestion API Tab */}
      {activeTabValue === 'aris-ingestion-api' && <ArisDataIngestionForm processId={processId} isPopupOpen={true} />}
    </div>
  )
}

export default DataIntegrationForm

